import $ from 'jquery'

/**
 * Each config has a type, status (enabled/disabled), and the values.
 */
class Config {
  constructor(type, id, active_intervals = null) {
    this.type = type
    this.enabled = false
    this.values = []
    this.active_intervals = active_intervals
  }

  /**
   * Make the config values an array of the method parameters.
   */
  setValues(...values) {
    this.values = [...values]
  }

  /**
   * Disable the config and set values to the empty string.
   */
  clear() {
    this.enabled = false
    this.setValues(...this.values.map(() => ''))
  }

  /**
   * Return an object that represents the config.
   */
  toObject() {
    const object = {
      type: this.type,
      enabled: this.enabled,
      values: this.values,
    }
    if (this.active_intervals) {
      object['active_intervals'] = this.active_intervals
    }

    return object
  }
}

/**
 * Config inputs describe the form input for a config.
 */
class ConfigInput {
  constructor(id) {
    this.id = id
    this.inputs = []
    this.notification = true
  }

  /**
   * Set whether the input is a notification or not.
   *
   * Notifications require contact information.
   */
  notify(notification = true) {
    this.notification = notification
    return this
  }

  /**
   * Add a named input field that the config get's values from.
   */
  take(...input) {
    this.inputs.push(...input)
    return this
  }

  /**
   * Check or uncheck this notifications checkbox that enables the notification.
   */
  check(checked = true) {
    $(`#${this.id}`).prop('checked', checked);
  }

  /**
   * Return whether this config input is checked or not.
   */
  checked() {
    return $(`#${this.id}`).is(':checked')
  }

  /**
   * Return an array of values for this config that are mapped by the transform.
   */
  getInputs(transform = (input) => input) {
    return this.inputs.map((name) => {
      const value = $(`input[name="${name}"]`).val()
      return transform(value)
    })
  }

  /**
   * Set the form values for this config with mapped values from the transform.
   */
  setInputs(values, transform = (input) => input) {
    this.inputs.forEach((name, i) => {
      if (values.length > i) {
        const value = transform(values[i])
        $(`input[name="${name}"]`).val(value)
      }
      else {
        $(`input[name="${name}"]`).val('')
      }
    })
  }
}

/**
 * Manage the data in the notifications modal form.
 */
class NotificationsModel {
  constructor() {
    this.types = {
      speed: new ConfigInput('notify_speed').take('notify_speed_limit'),
      offline: new ConfigInput('notify_offline'),
      is_moving: new ConfigInput('notify_moving'),
      temp: new ConfigInput('notify_temperature').take(
        'notify_temperature_min',
        'notify_temperature_max'
      ),
      batt: new ConfigInput('notify_battery').take('notify_battery_percent'),
      geofence: new ConfigInput('notify_geofence').take('notify_geofence_coordinates'),
      emails: new ConfigInput('notify_by_email').notify(false).take('notify_emails'),
      numbers: new ConfigInput('notify_by_sms').notify(false).take('notify_phones')
    }
    this.configs = {}
    for (const type in this.types) {
      this.configs[type] = new Config(type, this.types[type].id)
    }
  }

  /**
   * Convert a number input to an integer or ''.
   */
  static transformStringToNumber(value) {
    const parsedValue = parseInt(value)
    return isNaN(parsedValue) ? '' : parsedValue
  }

  /**
   * Convert a number to a string that can be set in a number input field.
   */
  static transformNumberToString(value) {
    return isNaN(value) ? '' : value.toString()
  }

  /**
   * Set the model data from the form values.
   */
  pull() {
    for (const type in this.types) {
      this.configs[type].enabled = this.types[type].checked()
    }
    this.configs['speed'].setValues(
      ...this.types['speed'].getInputs(NotificationsModel.transformStringToNumber)
    )
    this.configs['temp'].setValues(
      ...this.types['temp'].getInputs(NotificationsModel.transformStringToNumber)
    )
    this.configs['batt'].setValues(
      ...this.types['batt'].getInputs(NotificationsModel.transformStringToNumber)
    )
    this.configs['geofence'].setValues(
      ...this.types['geofence'].getInputs()
    )
    this.configs['emails'].setValues(
      ...this.splitMultipleEntries('notify_emails', ',')
    )
    this.configs['numbers'].setValues(
      ...this.splitMultipleEntries('notify_phones', ',')
    )

    // console.log('Pulled config:', this.toObject().config)
  }

  /**
   * Set the form values from the model data.
   */
  push() {
    for (const type in this.types) {
      this.types[type].check(this.configs[type].enabled)
    }
    this.types['speed'].setInputs(this.configs['speed'].values, NotificationsModel.transformNumberToString)
    this.types['temp'].setInputs(this.configs['temp'].values, NotificationsModel.transformNumberToString)
    this.types['batt'].setInputs(this.configs['batt'].values, NotificationsModel.transformNumberToString)
    this.types['geofence'].setInputs(this.configs['geofence'].values)

    const emails = this.configs['emails'].values.join(',')
    $('input[name="notify_emails"]').val(emails)

    const phones = this.configs['numbers'].values.join(',')
    $('input[name="notify_phones"]').val(phones)

    // console.log('Pushed config', this.toObject().config)
  }

  /**
   * Set the model data from a config object.
   */
  load(configs) {
    // console.log('Loading config', configs)
    this.clear()
    for (let i = 0; i < configs.length; i++) {
      const serverConfig = configs[i]
      const type = serverConfig.type
      if (type && this.configs[type]) {
        // The config is recognized and can be repopulated.
        const clientConfig = this.configs[type]
        if (serverConfig['active_intervals']) {
          clientConfig.active_intervals = serverConfig['active_intervals']
        }
        clientConfig.enabled = serverConfig.enabled
        clientConfig.setValues(...serverConfig.values)
      }
    }
    this.push()
  }

  /**
   * Clear the notifications form.
   */
  clear() {
    for (const type in this.types) {
      this.configs[type].clear()
    }
  }

  /**
   * Select/deselect all of the notification checkboxes.
   */
  toggleSelectAll() {
    const enabled = $('input[name="notify_select_all"').is(':checked')
    for (const type in this.types) {
      this.types[type].check(enabled)
    }
  }

  /**
   * Split fields that take multiple entries into an array of separate entries.
   */
  splitMultipleEntries(name, delimiter = ',') {
    return $(`input[name="${name}"]`).val().split(delimiter)
      .map((entry) => entry.trim())
      .filter((entry) => entry !== '')
  }

  /**
   * Return true if any notification is enabled.
   */
  hasNotifications() {
    let notifications = false
    for (const type in this.types) {
      if (this.configs[type].enabled && this.types[type].notification) {
        notifications = true
        break
      }
    }

    return notifications
  }

  /**
   * True if emails or phones are provided. False otherwise.
   */
  hasRecipients() {
    let contactsExist = false
    const hasEmails = this.configs['emails'].values.length > 0
    const hasNumbers = this.configs['numbers'].values.length > 0
    if (hasEmails || hasNumbers) {
      contactsExist = true
    }

    return contactsExist
  }

  /**
   * Return the model data as a standard object.
   */
  toObject() {
    const config = []
    for (const type in this.configs) {
      const input = this.types[type]
      const entry = this.configs[type].toObject()
      if (input.notification === false && entry.values.length > 0) {
        // Emails and phones are always enabled.
        entry.enabled = true
      }
      config.push(entry)
    }
    return { config }
  }
}

export { NotificationsModel }
